import { type ConnectorConfig, type ConnectorDataInfo, type ConnectorField } from '@/entities/connector';

type ConnectorDraft = {
  name?: string;
  config?: ConnectorConfig;
  dataInfo?: ConnectorDataInfo;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

export const isRequiredConfigFieldEmpty = (field: ConnectorField, value: unknown) => {
  switch (field.inputType) {
    case 'text':
    case 'password':
    case 'textarea':
      return value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
    case 'number':
      return value === undefined || value === null || value === '';
    case 'boolean':
      return value === undefined || value === null;
  }
};

export const hasIncompleteRequiredConfig = (fields: readonly ConnectorField[], config?: ConnectorConfig) => {
  return fields
    .filter((field) => field.required)
    .some((field) => isRequiredConfigFieldEmpty(field, config?.[field.label]));
};

export const isConnectorNameEmpty = (name?: string) => {
  return !name?.trim();
};

export const isConnectorValueEqual = (left: unknown, right: unknown): boolean => {
  if (Object.is(left, right)) {
    return true;
  }

  if (Array.isArray(left) && Array.isArray(right)) {
    return left.length === right.length && left.every((item, index) => isConnectorValueEqual(item, right[index]));
  }

  if (isPlainObject(left) && isPlainObject(right)) {
    const leftKeys = Object.keys(left).sort();
    const rightKeys = Object.keys(right).sort();

    return (
      leftKeys.length === rightKeys.length &&
      leftKeys.every((key, index) => {
        return rightKeys[index] === key && isConnectorValueEqual(left[key], right[key]);
      })
    );
  }

  return false;
};

export const isConnectorDraftEqual = (left: ConnectorDraft, right: ConnectorDraft) => {
  return (
    isConnectorValueEqual(left.name ?? '', right.name ?? '') &&
    isConnectorValueEqual(left.config ?? {}, right.config ?? {}) &&
    isConnectorValueEqual(left.dataInfo ?? {}, right.dataInfo ?? {})
  );
};
